import React from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Calendar, 
  CreditCard, 
  Mail,
  Phone,
  MapPin,
  Settings,
  TrendingUp
} from 'lucide-react';
import { Membership } from '@/lib/portal-api-client';

interface MembershipOverviewCardProps {
  membership: Membership;
  onUpdatePayment: () => void;
  onUpdateProfile: () => void;
  onRequestCancellation: () => void;
}

export function MembershipOverviewCard({ 
  membership, 
  onUpdatePayment,
  onUpdateProfile,
  onRequestCancellation
}: MembershipOverviewCardProps) {
  const visitsRemaining = membership.total_visits_allowed - membership.visits_used;
  const visitsProgress = (membership.visits_used / membership.total_visits_allowed) * 100;
  
  const isActive = membership.status === 'active';
  const isCancelled = membership.status === 'cancelled';

  const statusConfig = {
    active: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      label: 'Active',
    },
    cancelled: {
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      label: 'Cancelled',
    },
    suspended: {
      icon: XCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      label: 'Suspended',
    },
    expired: {
      icon: XCircle,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      label: 'Expired',
    },
  };

  const status = statusConfig[membership.status] || statusConfig.active;
  const StatusIcon = status.icon;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">{membership.plan_name}</h2>
            <p className="text-blue-100 text-sm">
              {membership.customer_first_name} {membership.customer_last_name}
            </p>
          </div>
          <div className={`${status.bgColor} ${status.color} px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-semibold`}>
            <StatusIcon className="w-4 h-4" />
            {status.label}
          </div>
        </div>

        {/* Plan Benefits */}
        {Array.isArray(membership.plan_features) && membership.plan_features.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Plan Benefits</h3>
            <ul className="space-y-2">
              {membership.plan_features.slice(0, 10).map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contact & Address */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Contact & Address</h3>
            {isActive && (
              <button
                onClick={onUpdateProfile}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                Edit
              </button>
            )}
          </div>

          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-700">
              <Mail className="w-4 h-4 text-gray-500" />
              <span>{membership.customer_email}</span>
            </div>

            <div className="flex items-center gap-2 text-gray-700">
              <Phone className="w-4 h-4 text-gray-500" />
              <span>{membership.customer_phone}</span>
            </div>

            <div className="flex items-start gap-2 text-gray-700">
              <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
              <div>
                <div>{membership.customer_address}</div>
                <div className="text-gray-600">
                  {membership.customer_city}, {membership.customer_state} {membership.customer_zip}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Billing Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm font-medium">Billing</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ${(membership.final_price_cents / 100).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              per {membership.billing_frequency === 'annual' ? 'year' : 'month'}
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Next Billing</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {new Date(membership.next_billing_date).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Visits Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">PM Visits This Year</span>
            </div>
            <span className="text-sm font-bold text-gray-900">
              {membership.visits_used} of {membership.total_visits_allowed}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(visitsProgress, 100)}%` }}
            />
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-600">
              {visitsRemaining > 0 ? (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  {visitsRemaining} visit{visitsRemaining !== 1 ? 's' : ''} remaining
                </span>
              ) : (
                <span className="text-orange-600 font-medium">All visits used</span>
              )}
            </span>
            <span className="text-xs text-gray-500">
              {membership.tune_ups_per_year} PM{membership.tune_ups_per_year !== 1 ? 's' : ''} per system/year
            </span>
          </div>
        </div>

        {/* Plan Details */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Membership Details</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <span className="text-gray-600">Plan Code:</span>
              <span className="ml-2 font-semibold text-gray-900">{membership.plan_code}</span>
            </div>
            <div>
              <span className="text-gray-600">Systems:</span>
              <span className="ml-2 font-semibold text-gray-900">{membership.systems_count}</span>
            </div>
            <div className="col-span-2">
              <span className="text-gray-600">Member Since:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {new Date(membership.start_date).toLocaleDateString('en-US', { 
                  month: 'long',
                  year: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {isActive && (
          <div className="flex gap-3">
            <button
              onClick={onUpdatePayment}
              className="flex-1 py-2.5 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Update Payment
            </button>
            <button
              onClick={onRequestCancellation}
              className="py-2.5 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {isCancelled && membership.cancellation_date && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <span className="font-semibold">Cancelled on:</span>{' '}
              {new Date(membership.cancellation_date).toLocaleDateString()}
            </p>
            {membership.cancellation_reason && (
              <p className="text-xs text-red-600 mt-1">
                Reason: {membership.cancellation_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
